import { useMemo, useState } from 'react';
import type { ExtractedEvents } from '../../av/events';
import type { AVSessionController } from '../../av/sessionController';
import { formatPosition, secondsToFrame, secondsToTicks, ticksToSeconds } from '../../av/time';
import * as av from '../../domain/avOps';
import { EVENT_CATEGORIES, type AvSession, type EventCategory, type Project } from '../../domain/schemas';
import type { SyncSelectionState, SyncSelectionStore } from '../../state/syncSelection';
import type { EditFn } from '../scene/useSceneEdit';
import { snapTicks } from './snap';
import { Button, inputClass } from '../ui';

export const EVENT_PAGE = 100;

/**
 * Extracted and manual events in pages of 100 rows (bounded DOM). Extracted events come
 * from the arrangement and cannot be edited here; manual markers can be added at the
 * playhead, renamed, moved (timeline drag) and removed.
 */
export function EventBrowser({ project, session, events, sync, sel, selection, edit }: { project: Project; session: AvSession; events: ExtractedEvents; sync: AVSessionController; sel: SyncSelectionState; selection: SyncSelectionStore; edit: EditFn }) {
  const [filter, setFilter] = useState<EventCategory | 'all' | 'notes'>('notes');
  const [page, setPage] = useState(0);
  const bpm = project.settings.bpm;
  const anim = session.animationId ? project.animations.find((a) => a.id === session.animationId) : undefined;
  const fps = anim?.fps ?? 24;
  const list = useMemo(() => {
    if (filter === 'all') return events.events;
    if (filter === 'notes') return events.events.filter((e) => e.source.kind === 'note' || e.source.kind === 'marker');
    return events.byCategory.get(filter) ?? [];
  }, [events, filter]);
  const pages = Math.max(1, Math.ceil(list.length / EVENT_PAGE));
  const p = Math.min(page, pages - 1);
  const rows = list.slice(p * EVENT_PAGE, (p + 1) * EVENT_PAGE);
  const selectedMarker = sel.eventId?.startsWith('marker:') ? session.markers.find((m) => `marker:${m.id}` === sel.eventId) : undefined;
  const addMarker = () => {
    const tick = snapTicks(secondsToTicks(sync.clock.seconds(), bpm), 'step');
    let id = '';
    if (
      edit('Add marker', (q) => {
        const r = av.addMarker(q, session.id, tick, `Marker ${session.markers.length + 1}`);
        id = r.id;
        return r.project;
      })
    )
      selection.update({ eventId: `marker:${id}` });
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <select className={`${inputClass} w-auto!`} value={filter} onChange={(e) => (setFilter(e.target.value as typeof filter), setPage(0))} aria-label="Show events" data-testid="event-filter">
          <option value="notes">Notes and markers</option>
          <option value="all">All events</option>
          {EVENT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {av.CATEGORY_LABELS[c]} ({events.byCategory.get(c)?.length ?? 0})
            </option>
          ))}
        </select>
        <Button size="sm" onClick={addMarker} data-testid="marker-add">
          + Marker at playhead
        </Button>
        <span className="text-xs text-zinc-400" data-testid="event-count">
          {list.length} events{events.truncated ? ' (truncated at the 20,000-event limit)' : ''}
        </span>
      </div>
      {selectedMarker && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-pink-950/40 p-2 text-xs">
          <label className="flex items-center gap-1 text-pink-200">
            Marker label
            <input className={`${inputClass} w-40!`} value={selectedMarker.label} maxLength={40} onChange={(e) => edit('Rename marker', (q) => av.renameMarker(q, session.id, selectedMarker.id, e.target.value), `marker:${selectedMarker.id}`)} data-testid="marker-label" />
          </label>
          <Button size="sm" variant="ghost" onClick={() => (edit('Remove marker', (q) => av.removeMarker(q, session.id, selectedMarker.id)), selection.update({ eventId: null }))} data-testid="marker-remove">
            Remove marker
          </Button>
          <span className="text-pink-300/80">Drag it on the timeline to move it (snaps to 16ths; Alt: free).</span>
        </div>
      )}
      <div className="max-h-64 overflow-auto rounded-md border border-zinc-800">
        <table className="w-full text-left text-xs" data-testid="event-table">
          <thead className="sticky top-0 bg-zinc-900 text-zinc-400">
            <tr>
              <th className="px-2 py-1 font-medium">Event</th>
              <th className="px-2 py-1 font-medium">Position</th>
              <th className="px-2 py-1 font-medium">Seconds</th>
              <th className="px-2 py-1 font-medium">Frame</th>
              <th className="px-2 py-1 font-medium">Vel.</th>
              <th className="px-2 py-1 font-medium">Source</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr
                key={e.id}
                className={`cursor-pointer border-t border-zinc-800/60 ${e.id === sel.eventId ? 'bg-zinc-700/60' : 'hover:bg-zinc-800/60'}`}
                onClick={() => (selection.update({ eventId: e.id }), void sync.seek(e.seconds))}
                aria-selected={e.id === sel.eventId}
                data-testid="event-row"
                data-event-id={e.id}
              >
                <td className="px-2 py-0.5">
                  <button type="button" className="text-left" onClick={(ev) => (ev.stopPropagation(), selection.update({ eventId: e.id }), void sync.seek(e.seconds))}>
                    {av.CATEGORY_LABELS[e.category]}
                    {e.source.kind === 'marker' && e.source.label ? ` “${e.source.label}”` : ''}
                  </button>
                </td>
                <td className="px-2 py-0.5 font-mono">{formatPosition(e.tick)}</td>
                <td className="px-2 py-0.5 font-mono">{e.seconds.toFixed(3)}</td>
                <td className="px-2 py-0.5 font-mono">{secondsToFrame(e.seconds - ticksToSeconds(session.animationStartTick, bpm), fps)}</td>
                <td className="px-2 py-0.5 font-mono">{e.velocity.toFixed(2)}</td>
                <td className="px-2 py-0.5">
                  <span className={`rounded px-1 ${e.source.kind === 'marker' ? 'bg-pink-900/70 text-pink-100' : 'bg-zinc-800 text-zinc-300'}`}>{e.source.kind === 'marker' ? 'manual' : 'extracted'}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <Button size="sm" onClick={() => setPage(p - 1)} disabled={p === 0}>
            ‹ Prev
          </Button>
          Page {p + 1} / {pages}
          <Button size="sm" onClick={() => setPage(p + 1)} disabled={p >= pages - 1}>
            Next ›
          </Button>
        </div>
      )}
    </div>
  );
}
